import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, cx, ErrorText, PageHeader } from "../../ui";
import { IconButton } from "../../ui/icons";
import { useNames } from "./ActivityRow";
import { ActivityView } from "./ActivityView";
import { BulkBar } from "./BulkBar";
import { Composer } from "./Composer";
import { HelpOverlay, PromptDialog, ShareDialog } from "./dialogs";
import { GitHubHeader, type OnSource } from "./GitHubViews";
import { threadSender } from "./SenderKind";
import { useListScroll } from "./list-scroll";
import { commandFor, isTyping } from "./keyboard";
import { boxLabel, returnAction } from "./mailboxes";
import { MessageCard } from "./MessageCard";
import { earlierTexts } from "./quoted";
import { kindLabel } from "./kinds";
import { scopedMessageIds } from "./model";
import { viewQuery, type InboxSearch } from "./scope";
import { SearchBar } from "./SearchBar";
import { SearchResults } from "./SearchResults";
import { useSelection, type SelectionState } from "./selection";
import { SnoozeDialog } from "./SnoozeDialog";
import { ThreadList } from "./ThreadList";
import { MessageStack, ThreadView, usePrefetchThread } from "./ThreadView";
import { useInbox, useNow } from "./use-inbox";
import { useBulk, type BulkTarget } from "./use-bulk";
import { useInboxCommands, type DialogState } from "./use-inbox-commands";

/**
 * The unified inbox (WP-007, architecture §1, §6.4, §8): the scope from the top bar, quick
 * filters, search, the live conversation list, the conversation view and the composer, with
 * keyboard triage throughout (`?` lists the keys), bulk selection with a bulk bar, and snooze.
 * From the `lg` breakpoint it is a split pane: the list on the left and the open conversation
 * beside it; narrower, the list and the conversation take turns.
 */
export function InboxPage({
  workspaceId,
  search,
  onSearch,
}: {
  workspaceId: string;
  search: InboxSearch;
  onSearch: (patch: Partial<InboxSearch>) => void;
}) {
  const now = useNow();
  const inbox = useInbox(workspaceId, search);
  const searchRef = useRef<HTMLInputElement>(null);
  const order = useMemo(() => inbox.entries.map((e) => e.id), [inbox.entries]);
  const selection = useSelection(
    JSON.stringify([search.scope, inbox.box, search.filter, search.tag, search.kind, search.app, search.gh, search.item, search.repo, search.q, workspaceId]),
    order,
  );
  const { toggle } = selection;
  const selectRow = useCallback((index: number) => toggle(index, false), [toggle]);
  const cmd = useInboxCommands({ workspaceId, inbox, search, onSearch, searchRef, onSelectRow: selectRow });
  const { run, dialog, after, setAfter, setDialog } = cmd;
  const view = inbox.open.data;
  // Checkboxes only while selecting: the Select button, or once anything is selected (keyboard).
  const [selectMode, setSelectMode] = useState(false);
  const selecting = selectMode || selection.sel.ids.size > 0 || selection.sel.all;
  useListScroll(search.t !== undefined || search.a !== undefined);
  const nameOf = useNames(inbox.entries.flatMap((e) => (e.kind === "activity" ? [e.item.workspaceId] : [])));

  useEffect(() => {
    if (dialog !== null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const command = commandFor(
        { key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, altKey: e.altKey, typing: isTyping(e.target) },
        search.t === undefined && search.a === undefined ? "list" : "thread",
      );
      if (command === null) return;
      e.preventDefault();
      run(command);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [dialog, run, search.t, search.a]);

  // "r" or "f" pressed on a list row: the conversation opened; compose once its bodies are here.
  useEffect(() => {
    if (after === null || view === undefined) return;
    setAfter(null);
    run(after);
  }, [after, view, setAfter, run]);

  if (inbox.scope === null) {
    return (
      <div>
        <PageHeader title="Inbox" />
        <p className="text-sm text-muted">
          That saved scope no longer exists.{" "}
          <Link to="/w/$workspaceId/inbox" params={{ workspaceId }} className="text-accent underline">
            Show this workspace’s inbox
          </Link>
        </p>
      </div>
    );
  }

  const open = search.t !== undefined || search.a !== undefined;
  return (
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,30rem)_minmax(0,1fr)] lg:items-start">
      <div className={cx("min-w-0 flex-col gap-2", open ? "hidden lg:flex" : "flex")}>
        <h1 className="sr-only">{boxLabel(inbox.box)}</h1>
        <SearchBar
          query={search.q}
          placeholder="Search mail — Enter to search"
          searchRef={searchRef}
          onSearch={(q) => onSearch({ q })}
        >
          <IconButton
            icon="select"
            label="Select conversations"
            className="size-8"
            aria-pressed={selecting}
            onClick={() => {
              if (selecting) {
                selection.clear();
                setSelectMode(false);
              } else setSelectMode(true);
            }}
          />
          <IconButton icon="compose" label="Compose" title="Compose (c)" tone="primary" className="size-8" onClick={() => run("compose")} aria-keyshortcuts="c" />
        </SearchBar>
        {search.q === undefined ? (
          <>
            <ListBulkBar inbox={inbox} search={search} selection={selection} />
            <ListSection
              inbox={inbox}
              cmd={cmd}
              now={now}
              onSource={(view) => onSearch({ kind: undefined, app: view.app, gh: view.gh, item: view.item, repo: view.repo, t: undefined })}
              nameOf={nameOf}
              selection={selection}
              onCompose={() => run("compose")}
              selecting={selecting}
              onDoneSelecting={() => {
                selection.clear();
                setSelectMode(false);
              }}
            />
          </>
        ) : (
          <SearchResults
            query={search.q}
            scope={inbox.searchArgs}
            now={now}
            suggest={inbox.suggest}
            onOpen={(hit) => cmd.openThread({ id: hit.threadId })}
          />
        )}
      </div>
      <div className={cx("min-w-0 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto", open ? "block" : "hidden lg:block")}>
        <ReadingPane open={open} inbox={inbox} search={search} cmd={cmd} nameOf={nameOf} now={now} />
      </div>
      <Dialogs dialog={dialog} inbox={inbox} mutate={cmd.mutate} onClose={() => setDialog(null)} />
    </div>
  );
}

function returnLabelOf(box: Parameters<typeof returnAction>[0]): string | null {
  const back = returnAction(box);
  return back === null ? null : back.label;
}

/** Beside the list: the open conversation, chat or task, or a hint to pick one. */
function ReadingPane({
  open,
  inbox,
  search,
  cmd,
  nameOf,
  now,
}: {
  open: boolean;
  inbox: ReturnType<typeof useInbox>;
  search: InboxSearch;
  cmd: ReturnType<typeof useInboxCommands>;
  nameOf: (uid: string) => string;
  now: Date;
}) {
  const { run } = cmd;
  return (
    <>
        {!open ? (
          <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted">Select a conversation to read it here.</p>
        ) : search.a !== undefined ? (
          inbox.openItem === undefined ? (
            <p className="p-4 text-sm text-muted">{inbox.activity.status === "loading" ? "Loading…" : "That is no longer in your inbox."}</p>
          ) : (
            <ActivityView
              key={inbox.openItem.id}
              item={inbox.openItem}
              nameOf={nameOf}
              now={now}
              onBack={cmd.back}
              onDone={() => {
                if (inbox.openItem !== undefined) cmd.finish(inbox.openItem);
              }}
            />
          )
        ) : (
          <ThreadView
            thread={inbox.open}
            subject={inbox.row === undefined ? null : inbox.row.subject}
            starred={inbox.row?.starred === true}
            snoozed={inbox.row !== undefined && inbox.row.snoozedUntil !== null}
            returnLabel={returnLabelOf(inbox.box)}
            kindReason={inbox.row === undefined ? null : inbox.row.kindReason}
            sender={inbox.row === undefined ? null : threadSender(inbox.row, inbox.accountInfo.get(inbox.row.accountId)?.address ?? null)}
            kind={inbox.row === undefined ? null : inbox.row.kind}
            newSender={inbox.row?.newSender === true}
            onBack={cmd.back}
            onCommand={run}
          >
            {(loaded) => (
              <MessageStack
                key={loaded.id}
                view={loaded}
                render={(m, expanded, toggle) => (
                  <MessageCard key={m.id} message={m} expanded={expanded} onToggle={toggle} earlierTexts={earlierTexts(loaded.messages, m)} />
                )}
              />
            )}
          </ThreadView>
        )}
    </>
  );
}

/** The bulk bar over the conversation list, while conversations are selected. */
function ListBulkBar({ inbox, search, selection }: { inbox: ReturnType<typeof useInbox>; search: InboxSearch; selection: SelectionState }) {
  const bulk = useBulk(selection.clear);
  const { sel } = selection;
  const picked = inbox.visible.filter((t) => sel.ids.has(t.id));
  // Search cannot leave an app out, so "select all matching" is not offered while All mail hides one.
  const query = inbox.hiding === null ? viewQuery(inbox.box, search.filter, search.tag, inbox.mailKind, inbox.source) : null;
  if (picked.length === 0 && !sel.all) return null;
  const target: BulkTarget =
    sel.all && query !== null
      ? { kind: "query", query, scope: inbox.searchArgs }
      : { kind: "selection", messageIds: picked.flatMap(scopedMessageIds), conversations: picked.length };
  const accountIds = [...new Set(picked.map((t) => t.accountId))];
  const back = returnAction(inbox.box);
  return (
    <BulkBar
      first={back === null ? undefined : { label: back.label, op: { kind: back.kind } }}
      count={picked.length}
      noun="conversation"
      allLoaded={inbox.visible.length > 0 && picked.length === inbox.visible.length}
      matching={sel.all}
      canMatch={query !== null}
      target={target}
      suggest={(picker) => inbox.suggest(picker, accountIds)}
      bulk={bulk}
      onSelectMatching={selection.selectMatching}
      onClear={selection.clear}
    />
  );
}

function ListSection({
  inbox,
  cmd,
  now,
  onSource,
  nameOf,
  selection,
  onCompose,
  selecting,
  onDoneSelecting,
}: {
  inbox: ReturnType<typeof useInbox>;
  cmd: ReturnType<typeof useInboxCommands>;
  now: Date;
  onSource: OnSource;
  nameOf: (uid: string) => string;
  selection: SelectionState;
  onCompose: () => void;
  selecting: boolean;
  onDoneSelecting: () => void;
}) {
  const { threads, entries } = inbox;
  const prefetch = usePrefetchThread();
  const summary = inbox.github === "none" ? null : inbox.github;
  const repos = githubRepos(inbox.visible, inbox.source === null ? undefined : inbox.source.repo);
  return (
    <section aria-label="Inbox" className="rounded-lg border border-border bg-surface">
      {/* The kinds are picked in the sidebar (WP-064); the list says which one it is. */}
      {inbox.source !== null ? (
        <GitHubHeader view={inbox.source} summary={summary} repos={repos} onSource={onSource} />
      ) : inbox.box === "inbox" ? (
        <div className="flex flex-wrap items-baseline gap-x-2 border-b border-border px-3 py-2">
          <h2 className="flex-1 text-sm font-semibold text-text">{kindLabel(inbox.mailKind)}</h2>
          {inbox.hiding === null ? null : (
            <p className="text-xs text-muted">
              GitHub hidden ·{" "}
              <button type="button" className="text-accent underline" onClick={() => inbox.setHidden("github", false)}>
                Show
              </button>
            </p>
          )}
        </div>
      ) : null}
      {threads.status === "loading" || inbox.scope === undefined ? <p className="p-4 text-sm text-muted">Loading conversations…</p> : null}
      {threads.status === "error" ? (
        <div className="p-4">
          <ErrorText>The inbox could not be loaded: {threads.message}</ErrorText>
        </div>
      ) : null}
      {threads.status === "ready" && entries.length === 0 && inbox.hiding !== null && threads.hasMore ? <p className="p-4 text-sm text-muted">Loading conversations…</p> : null}
      {threads.status === "ready" && entries.length === 0 && !(inbox.hiding !== null && threads.hasMore) ? (
        <Empty noAccounts={inbox.accountsLoaded && inbox.accounts.length === 0 && inbox.activity.status === "ready" && inbox.activity.items.length === 0} onCompose={onCompose} />
      ) : null}
      {threads.status === "ready" && entries.length > 0 ? (
        <ThreadList
          entries={entries}
          unread={inbox.activity.unread}
          nameOf={nameOf}
          accounts={inbox.accountInfo}
          cursor={Math.min(cmd.cursor, entries.length - 1)}
          focusCursor={cmd.focusTick}
          now={now}
          hasMore={threads.hasMore}
          loadingMore={threads.loadingMore}
          onLoadMore={threads.loadMore}
          onOpen={(t) => {
            cmd.setCursor(entries.findIndex((e) => e.id === t.id));
            cmd.openThread(t);
          }}
          onOpenActivity={(item) => {
            cmd.setCursor(entries.findIndex((e) => e.id === item.id));
            cmd.openActivity(item);
          }}
          onPrefetch={(t) => prefetch(t.id)}
          onCursor={cmd.setCursor}
          selected={selection.sel.ids}
          onSelect={selection.toggle}
          onSelectAll={selection.toggleAll}
          onUnsnooze={(t) => cmd.mutate({ kind: "unsnooze" }, t)}
          selecting={selecting}
          onDoneSelecting={onDoneSelecting}
          sent={inbox.box === "sent"}
        />
      ) : null}
    </section>
  );
}

function Empty({ noAccounts, onCompose }: { noAccounts: boolean; onCompose: () => void }) {
  if (noAccounts) {
    return (
      <div className="flex flex-col items-center gap-3 p-8 text-center">
        <p className="text-sm text-muted">No mail account is connected yet.</p>
        <Link to="/settings/accounts" className="text-sm font-medium text-accent underline">
          Connect an account
        </Link>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-3 p-8 text-center">
      <p className="text-sm text-muted">Nothing here. Inbox zero.</p>
      <Button onClick={onCompose}>Compose</Button>
    </div>
  );
}

function Dialogs({
  dialog,
  inbox,
  mutate,
  onClose,
}: {
  dialog: DialogState;
  inbox: ReturnType<typeof useInbox>;
  mutate: ReturnType<typeof useInboxCommands>["mutate"];
  onClose: () => void;
}) {
  if (dialog === null) return null;
  switch (dialog.kind) {
    case "help":
      return <HelpOverlay onClose={onClose} />;
    case "compose":
      return <Composer request={dialog.request} accounts={inbox.accounts} defaultFrom={inbox.defaultFrom} onClose={onClose} />;
    case "share":
      return <ShareDialog workspaceId={dialog.workspaceId} threadId={dialog.threadId} subject={dialog.subject} onClose={onClose} />;
    case "snooze":
      return <SnoozeDialog now={new Date()} onSnooze={(until) => mutate({ kind: "snooze", until }, dialog.target)} onClose={onClose} />;
    case "tag":
      return (
        <PromptDialog
          title="Add a tag"
          label="Tag"
          submitLabel="Add tag"
          suggestions={inbox.suggest("tag", [dialog.target.accountId])}
          onSubmit={(tag) => mutate({ kind: "addTags", tags: [tag] }, dialog.target)}
          onClose={onClose}
        />
      );
    case "move":
      return (
        <PromptDialog
          title="Move to folder"
          label="Folder"
          submitLabel="Move"
          suggestions={inbox.suggest("move", [dialog.target.accountId])}
          onSubmit={(folder) => mutate({ kind: "moveToFolder", folder }, dialog.target)}
          onClose={onClose}
        />
      );
  }
}

/** The repositories of the conversations listed, and the one chosen, for GitHub's filter. */
function githubRepos(threads: ReadonlyArray<{ source: { repo: string | null } | null }>, chosen: string | undefined): string[] {
  const repos = new Set(threads.flatMap((t) => (t.source === null || t.source.repo === null ? [] : [t.source.repo])));
  if (chosen !== undefined) repos.add(chosen);
  return [...repos].sort();
}
